import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  BookOpen,
  ClipboardList,
  ClipboardCheck,
  MessageSquare,
  AlertTriangle,
  BarChart3,
  UserCircle,
  LogOut,
  GraduationCap,
  School,
  Settings,
  Calendar,
  FileText,
  TrendingUp,
  Bell,
  ShieldAlert,
  HelpCircle,
  BookMarked,
  Briefcase,
  UserPlus,
  Target,
  Layers,
  ShieldCheck
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import Logo from '../Common/Logo';
import { getFileUrl } from '../../utils/api';


/**
 * Sidebar
 * - onOpenChatbot: callback from MainLayout to open the AI support chatbot
 */
const Sidebar = ({ isOpen, toggleSidebar, onOpenChatbot }) => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();

  const checkActive = (item) => {
    if (!item.path) return false;
    const currentPath = location.pathname;
    if (item.path === '/dashboard') {
      return [
        '/dashboard',
        '/admin/dashboard',
        '/admin/dashboard-new',
        '/college/dashboard',
        '/student/dashboard',
        '/recruiter/dashboard'
      ].includes(currentPath);
    }
    return currentPath === item.path || currentPath.startsWith(item.path + '/');
  };

  const menuItems = [
    {
      name: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
      // 'support' intentionally excluded — support role only sees Tickets Dashboard
      roles: ['admin', 'college_admin', 'student', 'recruiter', 'office_admin'],
    },
    {
      name: 'Institutes',
      icon: Building2,
      path: '/colleges',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'Institute Chain',
      icon: School,
      path: '/institute-chain',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'Manage Batches',
      icon: Layers,
      path: '/manage-batches',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'Users',
      icon: Users,
      path: '/users',
      roles: ['admin'],
    },
    {
      name: 'Onboard Student',
      icon: UserPlus,
      path: '/admin/student-onboarding',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'Courses',
      icon: BookOpen,
      path: '/courses',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'Assessments',
      icon: ClipboardList,
      path: '/assessments',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'Recruiters',
      icon: Briefcase,
      path: '/admin/recruiters',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'IT Support',
      icon: HelpCircle,
      path: '/it-support',
      roles: ['admin', 'office_admin'],
    },
    {
      name: 'Manage Jobs',
      icon: Briefcase,
      path: '/recruiter/jobs',
      roles: ['recruiter'],
    },
    {
      name: 'Applicants',
      icon: Users,
      path: '/recruiter/applicants',
      roles: ['recruiter'],
    },
    {
      name: 'Job Fairs',
      icon: Briefcase,
      path: '/recruiter/job-fairs',
      roles: ['recruiter'],
    },
    {
      name: 'Career Agent Analytics',
      icon: Target,
      path: '/admin/career-analytics',
      roles: ['admin'],
    },
    {
      name: 'Proctor',
      icon: ShieldCheck,
      path: '/admin/proctor',
      roles: ['admin'],
    },
    {
      name: 'Grievance Redressal',
      icon: ShieldAlert,
      path: '/admin/grievances',
      roles: ['admin'],
    },
    {
      name: 'Students',
      icon: GraduationCap,
      path: '/college/students',
      roles: ['college_admin'],
    },
    {
      name: 'Academic Records',
      icon: FileText,
      path: '/college/upload-cgpa',
      roles: ['college_admin'],
    },
    {
      name: 'Placement',
      icon: Briefcase,
      path: '/college/placement',
      roles: ['college_admin'],
    },
    {
      name: 'Performance Analytics',
      icon: TrendingUp,
      path: '/college/analytics',
      roles: ['college_admin'],
    },
    {
      name: 'Career Agent Analytics',
      icon: Target,
      path: '/college/career-analytics',
      roles: ['college_admin'],
    },
    {
      name: 'Academic Calendar',
      icon: Calendar,
      path: '/college/calendar',
      roles: ['college_admin'],
    },
    {
      name: 'Reports & Export',
      icon: FileText,
      path: '/college/reports',
      roles: ['college_admin'],
    },
    {
      name: 'Notifications',
      icon: Bell,
      path: '/college/notifications',
      roles: ['college_admin'],
    },
    {
      name: 'Micro Assessments',
      icon: ClipboardCheck,
      path: '/student/micro-assessments',
      roles: ['student'],
    },
    {
      name: 'Job Fairs',
      icon: Briefcase,
      path: '/student/job-fairs',
      roles: ['student'],
    },
    {
      name: 'Access Logs',
      icon: ShieldAlert,
      path: '/admin/access-logs',
      roles: ['admin'],
    },
    {
      name: 'Analytics',
      icon: BarChart3,
      path: '/analytics',
      roles: ['admin'],
    },
    {
      name: 'Community Hub',
      icon: MessageSquare,
      path: '/admin/community',
      roles: ['admin', 'college_admin', 'office_admin'],
    },
    {
      name: 'Tickets Dashboard',
      icon: MessageSquare,
      path: '/support/dashboard',
      roles: ['admin', 'support', 'office_admin'],
    },
    {
      name: 'IT Support',
      icon: HelpCircle,
      path: '/my-it-support',
      roles: ['college_admin', 'student'],
    },
    {
      name: 'Settings',
      icon: Settings,
      path: '/settings',
      roles: ['admin', 'college_admin', 'student', 'recruiter', 'support', 'office_admin'],
    },
  ];

  const filteredMenuItems = menuItems.filter((item) =>
    item.roles?.includes(user?.role)
  );

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 ${theme === 'dark' ? 'bg-[#00152E]' : 'bg-white'} shadow-2xl border-r border-gray-200/60 dark:border-[#1a3884]/30 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo Section with Plexus Background */}
          <div className={`relative overflow-hidden border-b border-gray-200/60 dark:border-[#1a3884]/30 p-6 flex items-center justify-center min-h-[100px] ${theme === 'dark' ? 'bg-gradient-to-br from-[#001736] via-[#00205B] to-[#012a66]' : 'bg-white'}`}>
            {theme === 'dark' && (
              <svg
                className="absolute inset-0 w-full h-full opacity-35 pointer-events-none"
                viewBox="0 0 200 80"
                preserveAspectRatio="none"
              >
                <line x1="25" y1="20" x2="60" y2="55" stroke="rgba(159, 181, 199, 0.25)" strokeWidth="0.75" />
                <line x1="60" y1="55" x2="95" y2="15" stroke="rgba(159, 181, 199, 0.25)" strokeWidth="0.75" />
                <line x1="95" y1="15" x2="135" y2="50" stroke="rgba(159, 181, 199, 0.25)" strokeWidth="0.75" />
                <line x1="135" y1="50" x2="165" y2="25" stroke="rgba(159, 181, 199, 0.25)" strokeWidth="0.75" />
                <line x1="165" y1="25" x2="190" y2="60" stroke="rgba(159, 181, 199, 0.25)" strokeWidth="0.75" />
                <line x1="60" y1="55" x2="110" y2="65" stroke="rgba(159, 181, 199, 0.2)" strokeWidth="0.75" />
                <line x1="110" y1="65" x2="135" y2="50" stroke="rgba(159, 181, 199, 0.2)" strokeWidth="0.75" />
                <line x1="135" y1="50" x2="165" y2="65" stroke="rgba(159, 181, 199, 0.15)" strokeWidth="0.75" />

                <circle cx="25" cy="20" r="2" fill="rgba(159, 181, 199, 0.5)" className="animate-pulse" />
                <circle cx="60" cy="55" r="2.5" fill="rgba(159, 181, 199, 0.6)" />
                <circle cx="95" cy="15" r="2" fill="rgba(159, 181, 199, 0.5)" />
                <circle cx="135" cy="50" r="3" fill="rgba(159, 181, 199, 0.7)" />
                <circle cx="165" cy="25" r="2" fill="rgba(159, 181, 199, 0.5)" />
                <circle cx="190" cy="60" r="1.5" fill="rgba(159, 181, 199, 0.4)" />
                <circle cx="110" cy="65" r="2" fill="rgba(159, 181, 199, 0.4)" />
                <circle cx="165" cy="65" r="1.5" fill="rgba(159, 181, 199, 0.3)" />
              </svg>
            )}

            <Link to="/dashboard" className="relative z-10 w-full block transform hover:scale-[1.02] transition-transform duration-200 pl-1">
              <Logo variant={theme === 'dark' ? 'light' : 'dark'} size="md" />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4 px-3 scrollbar-hide">
            <ul className="space-y-1">
              {filteredMenuItems.map((item) => {

                if (item.isChatbot) {
                  return (
                    <li key="it-support">
                      <button
                        onClick={() => {
                          if (window.innerWidth < 1024) toggleSidebar();
                          if (onOpenChatbot) onOpenChatbot();
                        }}
                        className="group flex items-center w-full px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 text-gray-700 dark:text-gray-300 hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/25 hover:text-[#1a3884] dark:hover:text-white hover:shadow-sm"
                      >
                        <item.icon className="w-5 h-5 mr-3 transition-all duration-200 group-hover:scale-110" />
                        <span className="font-medium flex-1 text-left">{item.name}</span>
                        <span className="ml-auto w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                      </button>
                    </li>
                  );
                }

                const isActive = checkActive(item);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`group flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                        ? 'bg-[#1a3884] !text-white shadow-md shadow-[#1a3884]/20'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/25 hover:text-[#1a3884] dark:hover:text-white hover:shadow-sm'
                        }`}
                      onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    >
                      <item.icon className="w-5 h-5 mr-3 transition-transform duration-200 group-hover:scale-110" />
                      <span className={`flex-1 text-left ${isActive ? 'font-semibold' : 'font-medium'}`}>{item.name}</span>
                      {isActive && (
                        <div className="ml-auto relative flex h-2.5 w-2.5 items-center justify-center">
                          <span
                            className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                            style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}
                          />
                          <span
                            className="relative inline-flex rounded-full h-1.5 w-1.5"
                            style={{ backgroundColor: '#ffffff' }}
                          />
                        </div>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User Profile & Logout */}
          <div className={`border-t border-gray-200/60 dark:border-[#1a3884]/30 p-4 ${theme === 'dark' ? 'bg-gradient-to-b from-[#002147] to-[#00152E]' : 'bg-gradient-to-b from-gray-50/50 to-white'}`}>
            <div className="flex items-center mb-3 p-3 rounded-xl bg-gradient-to-r from-[#00152E]/5 to-[#1a3884]/5 dark:from-[#1a3884]/10 dark:to-white/5 border border-[#1a3884]/10 dark:border-[#1a3884]/30 backdrop-blur-sm">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md shadow-[#1a3884]/20 ring-2 ring-white dark:ring-[#002147] overflow-hidden shrink-0 ${user?.profileImage || (user?.role === 'college_admin' && user?.college?.logo) ? 'bg-white' : 'bg-gradient-to-br from-[#00152E] to-[#1a3884] dark:from-[#002A5C] dark:to-[#1a3884]'}`}>
                {user?.profileImage ? (
                  <img src={getFileUrl(user.profileImage)} className="w-full h-full object-cover rounded-full" alt="Profile" />
                ) : user?.role === 'college_admin' && user?.college?.logo ? (
                  <img src={getFileUrl(user.college.logo)} className="w-full h-full object-cover rounded-full" alt="College Logo" />
                ) : (
                  <span className="text-white font-bold text-sm">
                    {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                )}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                  {user?.fullName || 'User'}
                </p>
                <p className="text-xs text-[#1a3884] dark:text-blue-300 capitalize font-medium">
                  {user?.role?.replace('_', ' ') || 'Admin'}
                </p>
              </div>
            </div>

            <button
              onClick={logout}
              className="flex items-center justify-center w-full px-4 py-2.5 text-sm font-semibold text-red-600 dark:text-red-200 bg-red-50 dark:bg-red-950/30 hover:bg-red-600 dark:hover:bg-red-900/60 hover:text-white dark:hover:text-red-100 rounded-xl transition-all duration-200 border border-red-200 dark:border-red-900/50 hover:border-red-500 dark:hover:border-red-600 shadow-sm hover:shadow-md hover:shadow-red-500/20 dark:hover:shadow-red-900/30 transform hover:scale-[1.01]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;